import type { Departure } from '@jums-dashboard/shared';

import Modal from '@/components/Modal/Modal';
import WidgetDetailChrome from '@/features/dashboard/components/WidgetDetailChrome';

import { minutesUntil, splitByReachability } from './reachability';
import useHslBusesConfig from './useHslBusesConfig';
import { useNow } from './useNow';

interface DetailProps {
  open: boolean;
  onClose: () => void;
  stopName?: string;
  departures: Departure[];
  lastUpdated?: Date | null;
  onRefresh?: () => void;
}

function fmtTime(timestamp: number): string {
  const d = new Date(timestamp);
  return d.toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' });
}

function fmtMin(minutes: number): string {
  if (minutes <= 0) return 'now';
  if (minutes === 1) return '1 min';
  return `${minutes} min`;
}

export default function HslBusesDetailModal({ open, onClose, stopName, departures, lastUpdated, onRefresh }: DetailProps) {
  const now = useNow(5_000);
  const { walkMinutes, leaveBufferMinutes } = useHslBusesConfig();
  const { unreachable, reachable, firstReachableKey } = splitByReachability(departures, now, walkMinutes);

  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose} title={stopName ?? 'Departures'} description="Upcoming HSL departures" size="md">
      <WidgetDetailChrome widgetId="hsl-buses" lastUpdated={lastUpdated} onRefresh={onRefresh} />
      <p className="mb-4 text-xs text-(--text-muted)">
        Walking time: {walkMinutes} min · Prep buffer: {leaveBufferMinutes} min. Buses you can't reach are dimmed.
      </p>

      {departures.length === 0 ? (
        <p className="text-sm text-(--text-tertiary)">No departures available.</p>
      ) : (
        <>
          {unreachable.length > 0 && (
            <section aria-label="Buses you cannot catch">
              <h3 className="mb-2 text-xs font-semibold tracking-wide text-(--text-muted) uppercase">Can't catch</h3>
              <ul className="divide-y divide-(--border-subtle)">
                {unreachable.map((d, i) => (
                  <DepartureRow
                    key={`${d.routeShortName}-${d.departureTimestamp}-${i}`}
                    departure={d}
                    now={now}
                    walkMinutes={walkMinutes}
                    leaveBufferMinutes={leaveBufferMinutes}
                    reachable={false}
                    highlighted={false}
                  />
                ))}
              </ul>
            </section>
          )}

          <section aria-label="Catchable buses" className={unreachable.length > 0 ? 'mt-4' : undefined}>
            <h3 className="mb-2 text-xs font-semibold tracking-wide text-(--text-muted) uppercase">Catchable</h3>
            {reachable.length === 0 ? (
              <p className="py-3 text-sm text-(--text-tertiary)">No reachable buses in the next 2h.</p>
            ) : (
              <ul className="divide-y divide-(--border-subtle)">
                {reachable.map((d, i) => (
                  <DepartureRow
                    key={`${d.routeShortName}-${d.departureTimestamp}-${i}`}
                    departure={d}
                    now={now}
                    walkMinutes={walkMinutes}
                    leaveBufferMinutes={leaveBufferMinutes}
                    reachable={true}
                    highlighted={`${d.routeShortName}-${d.departureTimestamp}` === firstReachableKey}
                  />
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </Modal>
  );
}

interface DepartureRowProps {
  departure: Departure;
  now: number;
  walkMinutes: number;
  leaveBufferMinutes: number;
  reachable: boolean;
  highlighted: boolean;
}

function DepartureRow({ departure: d, now, walkMinutes, leaveBufferMinutes, reachable, highlighted }: DepartureRowProps) {
  const mins = minutesUntil(d, now);
  const leaveIn = mins - walkMinutes - leaveBufferMinutes;

  const rowClasses = ['flex items-center gap-3 py-3 text-sm', !reachable && 'opacity-50', highlighted && 'font-semibold']
    .filter(Boolean)
    .join(' ');

  const ariaLabel = !reachable
    ? `Can't catch: ${d.routeShortName} to ${d.headsign}`
    : highlighted
      ? `Next catchable departure: ${d.routeShortName} to ${d.headsign}`
      : undefined;

  return (
    <li className={rowClasses} aria-label={ariaLabel}>
      <span className="font-display inline-flex h-8 min-w-10 items-center justify-center rounded-md bg-(--accent-primary)/15 font-semibold text-(--accent-primary) no-underline">
        {d.routeShortName}
      </span>
      <span className="flex-1 truncate text-(--text-secondary) no-underline">{d.headsign}</span>
      <span className="font-display text-(--text-muted) tabular-nums no-underline">{fmtTime(d.departureTimestamp)}</span>
      <span
        className={`font-display min-w-14 text-right tabular-nums no-underline ${highlighted ? 'text-(--accent-primary)' : 'text-(--text-primary)'}`}
      >
        {fmtMin(mins)}
      </span>
      <span className="min-w-20 text-right text-xs text-(--text-muted) no-underline">
        {reachable ? (leaveIn <= 0 ? 'leave now' : `leave in ${leaveIn} min`) : ''}
      </span>
      <span className="w-16 text-right text-xs text-(--text-muted) no-underline">{!d.realtime ? 'scheduled' : ''}</span>
    </li>
  );
}
